import React from 'react';
import { ArrowLeft, Plus, Minus, Check, Loader2, ChevronDown, Calendar, Trash2, Edit3, X, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { logFoodAPI, mealAPI } from '../../utils/api';
import { formatMacro } from '../../utils/nutritionFormat';
import { toast } from 'sonner';
import { getTodayString, formatDateString } from '../../utils/dateUtils';
import { resolveInitialServing } from './servingInit';
import { logServingDebug } from '../../utils/servingDebug';

/**
 * Select the best default serving unit from available options.
 * Prioritizes natural real-world servings over grams.
 * 
 * Priority order:
 * 1. Food-specific whole units (breast, fillet, steak, etc.)
 * 2. Common household measures (cup, tablespoon, teaspoon)
 * 3. Standard size names (large, medium — for eggs, fruits, etc.)
 * 4. Generic whole units (piece, serving, unit)
 * 5. Other containers (container, bowl, bottle, can)
 * 6. Fall back to grams/oz only if nothing else available
 */
function getBestDefaultUnit(servingOptions, fallbackUnit) {
  if (!servingOptions || servingOptions.length === 0) {
    return fallbackUnit || 'serving';
  }
  
  var availableUnits = servingOptions.map(function(opt) { return opt.unit; });
  
  // Priority 1: Food-specific whole units
  var wholeUnits = [
    'breast', 'thigh', 'wing', 'drumstick', 'leg',
    'fillet', 'steak', 'patty', 'cutlet',
    'banana', 'apple', 'orange', 'avocado',
    'egg', 'link', 'bar', 'cookie', 'muffin', 'bagel', 'roll'
  ];
  for (var i = 0; i < wholeUnits.length; i++) {
    if (availableUnits.indexOf(wholeUnits[i]) !== -1) {
      return wholeUnits[i];
    }
  }
  
  // Priority 2: Household measurement units (cup first — universal for vegetables,
  // grains, liquids; then tablespoon/teaspoon for condiments and spreads)
  // Use startsWith matching for compound units like "cup chopped", "cup sliced"
  var householdPrefixes = ['cup', 'tablespoon', 'teaspoon'];
  for (var h = 0; h < householdPrefixes.length; h++) {
    for (var hu = 0; hu < availableUnits.length; hu++) {
      if (availableUnits[hu] === householdPrefixes[h] || availableUnits[hu].indexOf(householdPrefixes[h]) === 0) {
        return availableUnits[hu];
      }
    }
  }
  
  // Priority 3: Standard size names (natural for eggs, fruits)
  var sizeUnits = ['large', 'medium'];
  for (var s = 0; s < sizeUnits.length; s++) {
    if (availableUnits.indexOf(sizeUnits[s]) !== -1) {
      return sizeUnits[s];
    }
  }
  
  // Priority 4: Generic whole units
  var genericUnits = ['piece', 'serving', 'unit'];
  for (var j = 0; j < genericUnits.length; j++) {
    if (availableUnits.indexOf(genericUnits[j]) !== -1) {
      return genericUnits[j];
    }
  }
  
  // Priority 5: Other containers
  var containerUnits = ['container', 'bowl', 'bottle', 'can'];
  for (var c = 0; c < containerUnits.length; c++) {
    if (availableUnits.indexOf(containerUnits[c]) !== -1) {
      return containerUnits[c];
    }
  }
  
  // Priority 6: Fall back to first available unit (often grams)
  return availableUnits[0] || fallbackUnit || 'serving';
}

// Continuous/scalable units — units where showing "(per [unit]: X)" is
// meaningful because the user can naturally scale by a fractional amount
// (e.g., halving a 20 fl oz drink is sensible). For discrete/count-based
// units (Individual, piece, serving, Kids, count, nugget...) the per-unit
// breakdown is confusing because the user thinks of the whole serving.
// Default for unknown units = NOT scalable (hide the hint).
var SCALABLE_UNITS = new Set([
  'g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms',
  'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds',
  'ml', 'milliliter', 'milliliters', 'l', 'liter', 'liters',
  'fl oz', 'fl', 'floz', 'fluid ounce', 'fluid ounces',
  'tbsp', 'tablespoon', 'tablespoons',
  'tsp', 'teaspoon', 'teaspoons',
  'cup', 'cups',
]);

function isScalableUnit(unit) {
  if (!unit) return false;
  return SCALABLE_UNITS.has(String(unit).toLowerCase().trim());
}

// Build the user-facing label for the per-unit hint, e.g. "fl oz" → "fl oz",
// "Grams" → "g". Normalises long forms to short forms for compactness.
function unitDisplayLabel(unit) {
  var u = String(unit || '').toLowerCase().trim();
  if (u === 'fl' || u === 'floz' || u === 'fluid ounce' || u === 'fluid ounces') return 'fl oz';
  if (u === 'gram' || u === 'grams') return 'g';
  if (u === 'kilogram' || u === 'kilograms') return 'kg';
  if (u === 'ounce' || u === 'ounces') return 'oz';
  if (u === 'pound' || u === 'pounds' || u === 'lbs') return 'lb';
  if (u === 'milliliter' || u === 'milliliters') return 'ml';
  if (u === 'liter' || u === 'liters') return 'l';
  if (u === 'tablespoon' || u === 'tablespoons') return 'tbsp';
  if (u === 'teaspoon' || u === 'teaspoons') return 'tsp';
  if (u === 'cups') return 'cup';
  return u;
}

function FoodConfirm(props) {
  var food = props.food;
  var onClose = props.onClose;
  var onSuccess = props.onSuccess;
  
  // ======= PACKAGED PRODUCT DETECTION =======
  // Phase 0 removed Spoonacular (`sp_prod_*`) and the legacy barcode prefix
  // (`upc_*`). Phase 1.B will set this true for FatSecret branded items —
  // detect by external_id prefix `fs_branded_*` (or `fs_food_*` if branded
  // metadata is present) once the FatSecret mapper ships. Until then the
  // packaged-product flow is intentionally inactive and falls through to the
  // generic serving_options path used by all other foods.
  var isPackagedItem = false;
  var hasPackageInfo = !!(isPackagedItem && food?.package_unit && food?.servings_per_container && food.servings_per_container > 0);
  
  // Build packaged item unit options.
  //
  // Semantic rules (Feb-2026 multipack fix):
  //   • "serving" is ALWAYS the smallest labeled portion a user eats in
  //     one sitting — e.g. 1 cup of a 3-cup frozen yogurt multipack. It
  //     is always offered first so it becomes the default selection
  //     (drives the common-case 150 cal default instead of 450 cal).
  //   • The package unit (bottle / can / bar / container / etc.) is
  //     only offered as an additional option when it's genuinely
  //     different from the serving — i.e. when servings_per_container > 1.
  //     For single-serve containers (SPC == 1), "1 container" equals
  //     "1 serving" nutritionally, so listing both is redundant.
  //   • For multipacks (SPC > 1) the pack-level unit is relabeled to
  //     "full pack (N servings)" so a user cannot mistake "1 container"
  //     for "one of the cups in the pack".
  var barcodeUnitOptions = [];
  if (isPackagedItem) {
    var _spc = food?.servings_per_container || 1;
    var _pkg = food?.package_unit;

    // Always offer "serving" first → becomes the default selection.
    var _servLabel = 'serving';
    if (food?.serving_description) _servLabel += ' (' + food.serving_description + ')';
    barcodeUnitOptions.push({ unit: 'serving', label: _servLabel, multiplier: 1 });

    // Offer the full-pack option only when it's meaningfully different.
    if (_pkg && _spc > 1) {
      var _spcLabel = _spc % 1 === 0 ? String(_spc) : _spc.toFixed(1);
      barcodeUnitOptions.push({
        unit: _pkg,
        label: 'full pack (' + _spcLabel + ' servings)',
        multiplier: _spc,
      });
    }
  }

  // Determine the best default unit — always "serving" for packaged
  // items after the rule above; for FatSecret items prefer the result-row
  // unit (matches what the user just tapped on, e.g. "100 g"); fall back
  // to the existing priority helper for everything else.
  var _resolveDefaultUnit = function () {
    if (isPackagedItem) {
      return barcodeUnitOptions.length > 0 ? barcodeUnitOptions[0].unit : 'serving';
    }
    var opts = (food && food.serving_options) || [];
    if (opts.length > 0 && food && food.serving_unit) {
      var primary = opts.find(function (o) { return o.unit === food.serving_unit; });
      if (primary) return primary.unit;
    }
    return getBestDefaultUnit(opts, food && food.serving_unit);
  };
  var defaultUnit = _resolveDefaultUnit();

  // Pre-compute the option matching defaultUnit so we can seed quantity +
  // nutrition consistently. Falls back to food.nutrition + food.serving_size.
  var _defaultOption = !isPackagedItem
    ? ((food && food.serving_options) || []).find(function (o) { return o.unit === defaultUnit; })
    : null;
  
  // ALL HOOKS MUST BE DECLARED AT THE TOP - before any early returns
  // Basic state. Default the displayed quantity to the matching option's
  // natural size (e.g. 100 for "100 g"), so the UI shows "100 grams · 195
  // cal" rather than "1 grams · 195 cal". For unitless / single-piece
  // servings (1 breast, 1 cup) the natural size is 1 and the display
  // collapses to the historical "1 × per-unit-macros" behavior.
  var rawServingSize = (food && typeof food.serving_size === 'number' && food.serving_size > 0) ? food.serving_size : 1;
  // Consistency guard (see servingInit.js): when the default option matches the food's serving unit
  // but its own `size` disagrees with `serving_size` (e.g. an "8 fl oz" product arriving with a
  // fl-oz option of size:1 carrying the full 8 fl oz nutrition), serving_size wins for the quantity
  // and per-unit nutrition is rescaled so the initial total still equals ONE serving.
  var _seed = resolveInitialServing(food, _defaultOption, isPackagedItem);
  var initialQuantity = _seed.quantity;
  var _initialNutrition = _seed.perUnit;
  var [quantity, setQuantity] = React.useState(initialQuantity);
  var [loading, setLoading] = React.useState(false);
  var [selectedDate, setSelectedDate] = React.useState(props.date || getTodayString());
  // True once the user explicitly picks a date here; until then we mirror the parent's
  // reactive `date` prop (LogFood's live current day) so a log after local midnight lands
  // on the new day. An explicit fixed date from the parent is also mirrored.
  var [dateManuallyPicked, setDateManuallyPicked] = React.useState(false);
  var [selectedMealType, setSelectedMealType] = React.useState(props.mealType);
  var [showDatePicker, setShowDatePicker] = React.useState(false);

  // Keep the log date in sync with the parent's reactive `date` prop until the user picks
  // one manually (mirrors LogFood's live current-day value across midnight / app-resume).
  React.useEffect(function() {
    if (!dateManuallyPicked && props.date && props.date !== selectedDate) {
      setSelectedDate(props.date);
    }
  }, [props.date, dateManuallyPicked]); // eslint-disable-line react-hooks/exhaustive-deps
  var [showMealPicker, setShowMealPicker] = React.useState(false);
  
  // Unit selection state - use best default unit. With Spoonacular gone,
  // every food we receive ships its own serving_options; no async unit
  // resolution is needed. Phase 1.B can re-enable this only if FatSecret
  // returns items with empty serving_options (unlikely per their docs).
  var needsUnitResolution = false;
  var [selectedUnit, setSelectedUnit] = React.useState(defaultUnit);
  var [showUnitPicker, setShowUnitPicker] = React.useState(false);
  var [unitLoading, setUnitLoading] = React.useState(false);
  // Seed currentNutrition from the matching serving_options entry when
  // available — this is what makes the unit picker's first render show
  // correct per-1-unit macros for the chosen unit (e.g. per-1-g for the
  // "100 g" default option). For foods without serving_options we fall
  // back to the food's own nutrition (already per-unit via the mapper).
  // (per-unit nutrition already resolved above via resolveInitialServing -> _initialNutrition)
  var [currentNutrition, setCurrentNutrition] = React.useState(_initialNutrition);
  var [initialLoadDone, setInitialLoadDone] = React.useState(false);
  var [fullNutritionLoaded, setFullNutritionLoaded] = React.useState(false);

  // Flag-gated diagnostics: capture the final food object + initial serving resolution exactly as
  // FoodConfirm receives/computes it (no serving math changed). No-op unless debug mode is on.
  React.useEffect(function () {
    logServingDebug('FoodConfirmInitialResolution', {
      food: food && {
        external_id: food.external_id, source: food.source, brand: food.brand,
        serving_size: food.serving_size, serving_unit: food.serving_unit,
        serving_description: food.serving_description, nutrition: food.nutrition,
        serving_options: food.serving_options,
      },
      isPackagedItem: isPackagedItem,
      selected_default_unit: defaultUnit,
      selected_default_option: _defaultOption,
      initial_quantity: initialQuantity,
      initial_per_unit_nutrition: _initialNutrition,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Dynamic serving options (updated from API when initial options are empty)
  var [dynamicServingOptions, setDynamicServingOptions] = React.useState(null);
  
  // Gram weight for current unit (from Spoonacular weightPerServing)
  var [weightGrams, setWeightGrams] = React.useState(null);

  // Quantity editing state - MOVED TO TOP before early return
  var [isEditingQuantity, setIsEditingQuantity] = React.useState(false);
  var [editQuantityValue, setEditQuantityValue] = React.useState(String(initialQuantity));
  var quantityInputRef = React.useRef(null);

  // Ingredients state for custom meals - prefer structured ingredient_details
  var structuredIngredients = food?.ingredient_details || [];
  var textIngredients = food?.ingredients || [];
  
  var [mealIngredients, setMealIngredients] = React.useState(
    structuredIngredients.length > 0
      ? structuredIngredients.map(function(ing, idx) {
          return {
            id: idx,
            name: ing.name || '',
            food_id: ing.food_id || '',
            quantity: ing.quantity || 1,
            unit: ing.unit || 'serving',
            grams: ing.grams || 0,
            calories: ing.calories || 0,
            protein: ing.protein || 0,
            carbs: ing.carbs || 0,
            fat: ing.fat || 0,
            _perG: ing._perG || { cal: 0, protein: 0, carbs: 0, fat: 0 },
            portionOptions: ing.portionOptions || [],
            isStructured: true
          };
        })
      : textIngredients.map(function(ing, idx) {
          return { id: idx, text: typeof ing === 'string' ? ing : '', isStructured: false };
        })
  );
  var [showIngredients, setShowIngredients] = React.useState(
    structuredIngredients.length > 0 || textIngredients.length > 0
  );
  var hasIngredients = structuredIngredients.length > 0 || textIngredients.length > 0;
  var hasStructuredIngredients = structuredIngredients.length > 0;
  var isCustomMeal = food?.source === 'CUSTOM' || food?.source === 'MANUAL' || hasIngredients;
  var isManualEntry = food?.source === 'MANUAL';

  // Ingredient editing state
  var [editingIngredients, setEditingIngredients] = React.useState(false);
  var [addingIngredient, setAddingIngredient] = React.useState(false);
  var [ingredientSearchQuery, setIngredientSearchQuery] = React.useState('');
  var [ingredientSearchResults, setIngredientSearchResults] = React.useState([]);
  var [searchingIngredients, setSearchingIngredients] = React.useState(false);

  // Track if user has edited ingredients (to avoid overwriting original macros)
  var [hasEditedIngredients, setHasEditedIngredients] = React.useState(false);

  // Editable macros state for custom meals
  var [editableMacros, setEditableMacros] = React.useState({
    calories: food?.nutrition?.calories || 0,
    protein: food?.nutrition?.protein_g || 0,
    carbs: food?.nutrition?.carbs_g || 0,
    fat: food?.nutrition?.fat_g || 0
  });

  // Editable name for manual entries
  var [editableName, setEditableName] = React.useState(food?.name || 'Custom Food');

  // Macro-calorie mismatch dialog state (manual entry only)
  var [showMismatchDialog, setShowMismatchDialog] = React.useState(false);

  // Computed calories from macros (4/4/9 rule)
  var computedCalories = (editableMacros.protein * 4) + (editableMacros.carbs * 4) + (editableMacros.fat * 9);
  var hasMismatch = isManualEntry && editableMacros.calories > 0 && Math.abs(editableMacros.calories - computedCalories) > 5;

  // Fetch full nutrition on mount.
  //
  // Phase 0 removed Spoonacular — every food we receive in Phase 0 / 1.A
  // already ships its full nutrition + serving_options inline (USDA core
  // foods, user_custom_meals, manual entries). No async fetch is needed.
  //
  // Phase 1.B may re-enable this branch if FatSecret returns items
  // requiring per-unit nutrition fetches; the call removed here was
  // `logFoodAPI.getFoodNutrition()` which itself was deleted in Phase 0.
  React.useEffect(function() {
    if (!food || initialLoadDone) return;
    setInitialLoadDone(true);
    setFullNutritionLoaded(true);
  }, [food, initialLoadDone]);

  // Focus input when editing starts
  React.useEffect(function() {
    if (isEditingQuantity && quantityInputRef.current) {
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
      var _el = quantityInputRef.current;
      setTimeout(function () {
        try { _el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_e) { /* noop */ }
      }, 300);
    }
  }, [isEditingQuantity]);

  // Update edit value when quantity changes from +/- buttons
  React.useEffect(function() {
    if (!isEditingQuantity) {
      setEditQuantityValue(String(quantity));
    }
  }, [quantity, isEditingQuantity]);

  // Ingredient search effect
  React.useEffect(function() {
    if (!ingredientSearchQuery || ingredientSearchQuery.length < 2) {
      setIngredientSearchResults([]);
      return;
    }
    
    var timeoutId = setTimeout(function() {
      setSearchingIngredients(true);
      mealAPI.searchCoreFoods(ingredientSearchQuery, 10)
        .then(function(response) {
          setIngredientSearchResults(response.data || []);
        })
        .catch(function(err) {
          console.error('Ingredient search error:', err);
          setIngredientSearchResults([]);
        })
        .finally(function() {
          setSearchingIngredients(false);
        });
    }, 300);
    
    return function() { clearTimeout(timeoutId); };
  }, [ingredientSearchQuery]);

  // Recalculate meal macros when ingredients change - ONLY if user has edited
  React.useEffect(function() {
    if (hasEditedIngredients && hasStructuredIngredients && mealIngredients.length > 0) {
      var totals = mealIngredients.reduce(function(acc, ing) {
        if (ing.isStructured) {
          return {
            calories: acc.calories + (ing.calories || 0),
            protein: acc.protein + (ing.protein || 0),
            carbs: acc.carbs + (ing.carbs || 0),
            fat: acc.fat + (ing.fat || 0)
          };
        }
        return acc;
      }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
      
      setEditableMacros(totals);
    }
  }, [mealIngredients, hasStructuredIngredients, hasEditedIngredients]);

  // Early return AFTER all hooks
  if (!food) return null;

  // Get available serving options (prefer dynamic API options over initial empty search results)
  // For barcode items, use barcodeUnitOptions; for others use API/search options
  var servingOptions;
  var hasMultipleUnits;
  if (isPackagedItem) {
    servingOptions = barcodeUnitOptions;
    hasMultipleUnits = barcodeUnitOptions.length > 1;
  } else {
    servingOptions = (dynamicServingOptions && dynamicServingOptions.length > 0) 
      ? dynamicServingOptions 
      : (food.serving_options || []);
    hasMultipleUnits = servingOptions.length > 1;
  }
  
  // ======= UNIFIED MACRO CALCULATION =======
  // For barcode items: look up the selected unit's nutrition multiplier
  var barcodeMultiplier = 1;
  if (isPackagedItem) {
    var matchedOption = barcodeUnitOptions.find(function(o) { return o.unit === selectedUnit; });
    barcodeMultiplier = matchedOption ? matchedOption.multiplier : 1;
  }
  
  var calories, protein, carbs, fat;
  if (isCustomMeal) {
    calories = Math.round(editableMacros.calories * quantity);
    protein = formatMacro(editableMacros.protein * quantity);
    carbs = formatMacro(editableMacros.carbs * quantity);
    fat = formatMacro(editableMacros.fat * quantity);
  } else if (isPackagedItem) {
    calories = Math.round((currentNutrition.calories || 0) * barcodeMultiplier * quantity);
    protein = formatMacro((currentNutrition.protein_g || 0) * barcodeMultiplier * quantity);
    carbs = formatMacro((currentNutrition.carbs_g || 0) * barcodeMultiplier * quantity);
    fat = formatMacro((currentNutrition.fat_g || 0) * barcodeMultiplier * quantity);
  } else {
    calories = Math.round((currentNutrition.calories || 0) * quantity);
    protein = formatMacro((currentNutrition.protein_g || 0) * quantity);
    carbs = formatMacro((currentNutrition.carbs_g || 0) * quantity);
    fat = formatMacro((currentNutrition.fat_g || 0) * quantity);
  }
  
  // Secondary micronutrients — render ONLY what FatSecret actually supplied.
  // `currentNutrition` carries sodium_mg / sugar_g / fiber_g only when present
  // (the mapper preserves absence as `undefined`, never 0). These come from the
  // SELECTED serving option, so switching units swaps them automatically; we
  // scale by quantity here exactly like the macros above. Never shown for
  // custom/manual entries (those use the editable-macro path, which has no
  // provider micros).
  var secondaryNutrients = [];
  if (!isCustomMeal) {
    if (currentNutrition.sodium_mg != null) {
      secondaryNutrients.push({ key: 'sodium', label: 'Sodium', value: Math.round(currentNutrition.sodium_mg * quantity).toLocaleString('en-US') + ' mg' });
    }
    if (currentNutrition.sugar_g != null) {
      secondaryNutrients.push({ key: 'sugar', label: 'Sugar', value: formatMacro(currentNutrition.sugar_g * quantity) + 'g' });
    }
    if (currentNutrition.fiber_g != null) {
      secondaryNutrients.push({ key: 'fiber', label: 'Fiber', value: formatMacro(currentNutrition.fiber_g * quantity) + 'g' });
    }
  }

  // 0 calories normally blocks logging (an unfinished blank Manual/Custom
  // entry). A provider food that carries real nutrient data is a legitimate
  // 0-cal item (e.g. a diet drink with sodium) and may be logged.
  var isValidZeroCalProviderFood = !isCustomMeal && secondaryNutrients.length > 0;

  var mealSlots = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

  // Unit-aware quantity bounds. Bulk weight/volume units (grams / milliliters)
  // routinely start well above the count-serving cap of 20 (e.g. a 150 g
  // Oikos serving), which previously left the "+" button permanently
  // disabled. For g/ml only we raise the step + caps; every other unit keeps
  // the exact original behavior (step 0.5, increment clamp 100, button cap 20,
  // edit range 0.1–100). No product-specific handling.
  var _isBulkUnit = /^(g|gram|grams|ml|milliliter|milliliters)$/i.test(selectedUnit || '');
  var _qtyStep = _isBulkUnit ? 5 : 0.5;
  var _qtyIncMax = _isBulkUnit ? 2000 : 100;   // clamp used by incrementQuantity
  var _qtyBtnMax = _isBulkUnit ? 2000 : 20;    // increase-button disable threshold
  var _qtyMin = _isBulkUnit ? _qtyStep : 0.5;  // decrement clamp + decrease-button disable
  var _qtyEditMin = _isBulkUnit ? 1 : 0.1;
  var _qtyEditMax = _isBulkUnit ? 2000 : 100;

  function incrementQuantity() { setQuantity(Math.min(quantity + _qtyStep, _qtyIncMax)); }
  function decrementQuantity() { setQuantity(Math.max(quantity - _qtyStep, _qtyMin)); }

  // Localized keyboard-avoidance: when an editable field gains focus, scroll
  // it into the center of the viewport after the iOS keyboard animation. This
  // is scoped to the focused element only — no global keyboard config or
  // app-wide layout changes.
  function scrollFieldIntoView(e) {
    var el = e && e.target;
    if (!el) return;
    setTimeout(function () {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_e) { /* noop */ }
    }, 300);
  }

  // Handle unit change.
  //
  // Phase 0 removed the Spoonacular per-unit nutrition fetch (the
  // `logFoodAPI.getFoodNutrition` helper itself is gone). Every food now
  // ships pre-calculated serving_options with macros baked in, so the
  // change is purely local — pick the matching option, swap in its
  // per-1-unit macros, and reset the quantity to that option's natural
  // size (e.g. 100 g → 100, 1 breast → 1).
  function handleUnitChange(newUnit) {
    setSelectedUnit(newUnit);
    setShowUnitPicker(false);
    var opts = (food && food.serving_options) || [];
    var opt = opts.find(function (o) { return o.unit === newUnit; });
    if (opt && opt.nutrition) {
      setCurrentNutrition(opt.nutrition);
    }
    if (opt && typeof opt.size === 'number' && opt.size > 0) {
      setQuantity(opt.size);
    } else {
      setQuantity(1);
    }
  }

  // Handle quantity edit submit
  function handleQuantityEditSubmit() {
    var parsed = parseFloat(editQuantityValue);
    if (!isNaN(parsed) && parsed >= _qtyEditMin && parsed <= _qtyEditMax) {
      setQuantity(Math.round(parsed * 10) / 10); // Round to 1 decimal
    } else {
      setEditQuantityValue(String(quantity)); // Reset to current value
    }
    setIsEditingQuantity(false);
  }

  function handleLog() {
    // For manual entries, check macro-calorie consistency before logging
    if (isManualEntry && hasMismatch) {
      setShowMismatchDialog(true);
      return;
    }
    executeLog();
  }

  function handleMismatchAdjust() {
    // Option A: Auto-adjust calories to match macros
    setEditableMacros(function(prev) {
      return {
        calories: computedCalories,
        protein: prev.protein,
        carbs: prev.carbs,
        fat: prev.fat
      };
    });
    setShowMismatchDialog(false);
    // Small delay so state updates before logging
    setTimeout(function() { executeLog(computedCalories); }, 50);
  }

  function handleMismatchOverride() {
    // Option B: Keep user's entered calories
    setShowMismatchDialog(false);
    executeLog();
  }

  function executeLog(overrideCalories) {
    setLoading(true);
    
    // Build nutrition object - always per-unit (before quantity multiplication)
    var nutritionToLog;
    if (isCustomMeal) {
      nutritionToLog = {
        calories: overrideCalories != null ? overrideCalories : editableMacros.calories,
        protein_g: editableMacros.protein,
        carbs_g: editableMacros.carbs,
        fat_g: editableMacros.fat
      };
    } else if (isPackagedItem && barcodeMultiplier !== 1) {
      // Selected unit represents more than 1 serving (e.g. "can" = 2.5 servings)
      nutritionToLog = {
        calories: Math.round((currentNutrition.calories || 0) * barcodeMultiplier),
        protein_g: Math.round((currentNutrition.protein_g || 0) * barcodeMultiplier),
        carbs_g: Math.round((currentNutrition.carbs_g || 0) * barcodeMultiplier),
        fat_g: Math.round((currentNutrition.fat_g || 0) * barcodeMultiplier)
      };
      // Carry secondary nutrients through the multiplier too, present-only
      // (absence stays absent so the backend never stores a 0).
      if (currentNutrition.sodium_mg != null) nutritionToLog.sodium_mg = currentNutrition.sodium_mg * barcodeMultiplier;
      if (currentNutrition.sugar_g != null) nutritionToLog.sugar_g = currentNutrition.sugar_g * barcodeMultiplier;
      if (currentNutrition.fiber_g != null) nutritionToLog.fiber_g = currentNutrition.fiber_g * barcodeMultiplier;
    } else {
      nutritionToLog = currentNutrition;
    }
    
    // Build ingredients list - handle both structured and text ingredients
    var ingredientsToLog = mealIngredients.map(function(ing) {
      if (ing.isStructured) {
        return ing.quantity + ' ' + ing.unit + ' ' + ing.name;
      }
      return ing.text || '';
    }).filter(function(t) { return t && t.trim(); });
    
    // Build ingredient_details for structured ingredients
    var ingredientDetailsToLog = mealIngredients.filter(function(ing) {
      return ing.isStructured;
    }).map(function(ing) {
      return {
        name: ing.name,
        food_id: ing.food_id,
        quantity: ing.quantity,
        unit: ing.unit,
        grams: ing.grams,
        calories: ing.calories,
        protein: ing.protein,
        carbs: ing.carbs,
        fat: ing.fat,
        _perG: ing._perG,
        portionOptions: ing.portionOptions
      };
    });
    
    // Create modified food object with current nutrition and unit
    var foodToLog = {
      ...food,
      name: isManualEntry ? editableName : food.name,
      serving_unit: selectedUnit,
      nutrition: nutritionToLog,
      // Include edited ingredients
      ingredients: ingredientsToLog,
      ingredient_details: ingredientDetailsToLog
    };
    
    var displayName = isManualEntry ? editableName : food.name;
    logFoodAPI.logFood(foodToLog, selectedDate, selectedMealType, quantity)
      .then(function() {
        toast.success(displayName + ' logged!');
        onSuccess();
      })
      .catch(function(err) {
        var status = err && err.response && err.response.status;
        var rawDetail = err && err.response && err.response.data && (err.response.data.detail || err.response.data.message);
        // FastAPI's 422 detail is an ARRAY of objects ({loc, msg, type, ...});
        // if we stringify it directly the user sees "[object Object], …".
        // Render the first field path + message instead, capped at 80 chars.
        var detail = '';
        if (Array.isArray(rawDetail) && rawDetail.length > 0) {
          var first = rawDetail[0] || {};
          var loc = Array.isArray(first.loc) ? first.loc.filter(function (p) { return p !== 'body'; }).join('.') : '';
          detail = (loc ? loc + ': ' : '') + (first.msg || 'invalid payload');
          if (rawDetail.length > 1) detail += ' (+' + (rawDetail.length - 1) + ' more)';
        } else if (rawDetail) {
          detail = String(rawDetail);
        }
        console.error('[FoodConfirm] log-food failed:', { status: status, rawDetail: rawDetail, message: err && err.message });
        if (status === 401) {
          toast.error('Session expired — please sign in again.');
        } else if (status) {
          toast.error('Failed to log food (HTTP ' + status + ')' + (detail ? ': ' + detail.slice(0, 80) : ''));
        } else {
          toast.error('Failed to log food — network unreachable.');
        }
      })
      .finally(function() {
        setLoading(false);
      });
  }

  // Generate date options
  var dateOptions = [];
  var today = new Date();
  for (var i = -7; i <= 7; i++) {
    var d = new Date(today);
    d.setDate(d.getDate() + i);
    var val = formatDateString(d);
    var label = i === 0 ? 'Today' : i === -1 ? 'Yesterday' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    dateOptions.push({ value: val, label: label });
  }

  var currentDateLabel = selectedDate;
  for (var j = 0; j < dateOptions.length; j++) {
    if (dateOptions[j].value === selectedDate) {
      currentDateLabel = dateOptions[j].label;
    }
  }

  // Format unit for display (singular). Count units carry singular+plural keys so a plural unit
  // string (e.g. "meatballs") still renders "1 meatball" (never "1 meatballs").
  function formatUnit(unit) {
    var unitMap = {
      'g': 'grams',
      'oz': 'ounces',
      'ml': 'milliliters',
      'cup': 'cup',
      'tbsp': 'tablespoon',
      'tsp': 'teaspoon',
      'piece': 'piece', 'pieces': 'piece',
      'slice': 'slice', 'slices': 'slice',
      'meatball': 'meatball', 'meatballs': 'meatball',
      'nugget': 'nugget', 'nuggets': 'nugget',
      'patty': 'patty', 'patties': 'patty',
      'link': 'link', 'links': 'link',
      'cookie': 'cookie', 'cookies': 'cookie',
      'cracker': 'cracker', 'crackers': 'cracker',
      'serving': 'serving',
      'can': 'can',
      'bottle': 'bottle',
      'bar': 'bar', 'bars': 'bar',
      'box': 'box',
      'bag': 'bag',
      'pouch': 'pouch',
      'jar': 'jar',
      'carton': 'carton',
      'tub': 'tub',
      'pack': 'pack',
      'packet': 'packet',
      'container': 'container',
      'package': 'package'
    };
    var key = String(unit == null ? '' : unit).toLowerCase().trim();
    return unitMap[key] || unit;
  }

  // Pluralize a display unit based on quantity
  function pluralizeUnit(unit, qty) {
    var singular = formatUnit(unit);
    if (qty === 1) return singular;
    // Already plural (ends in s/es)
    if (singular.endsWith('s')) return singular;
    // Handle irregular plurals
    if (singular === 'box') return 'boxes';
    if (singular === 'pouch') return 'pouches';
    if (singular === 'patty') return 'patties';
    // Regular: add 's'
    return singular + 's';
  }

  // Honest unit label. FatSecret sometimes returns the generic unit "serving"
  // while the real portion is a container (e.g. description "1 can"). Showing
  // "1 serving" then misrepresents a whole can as a standard serving. When the
  // unit is the generic "serving" AND a specific descriptor exists, derive the
  // label from the descriptor (strip the leading count) so it reads "1 can".
  function deriveServingNoun(desc) {
    if (!desc) return null;
    var raw = String(desc).trim();
    // Defensive guard: never treat a provider NUTRITION sentence
    // ("Per 1 serving - Calories: 210kcal | Fat: 10g | ...") as a serving
    // label. The backend sanitizes this at the source, but guard here too so
    // a verbose description can never render inside the quantity control.
    if (/calor|kcal|\bfat\b|\bcarb|\bprotein\b|\|/i.test(raw)) return null;
    var s = raw.replace(/^[\d.,/\s]+/, '').trim();
    if (!s || /^serving/i.test(s)) return null;
    // A real serving noun is short ("can", "taco", "cup chopped"); reject
    // anything sentence-like that slips through.
    if (s.length > 24 || s.split(/\s+/).length > 3) return null;
    return s;
  }
  function unitLabel(unit, qty) {
    if (unit === 'serving') {
      var noun = deriveServingNoun(food && food.serving_description);
      if (noun) {
        if (qty !== 1 && !/\s/.test(noun) && !noun.endsWith('s')) return noun + 's';
        return noun;
      }
    }
    return pluralizeUnit(unit, qty);
  }

  // Ingredient management functions for text-only ingredients
  function addIngredient() {
    setMealIngredients(function(prev) {
      return prev.concat([{ id: Date.now(), text: '', isStructured: false }]);
    });
  }

  function updateIngredient(id, text) {
    setMealIngredients(function(prev) {
      return prev.map(function(ing) {
        return ing.id === id ? { ...ing, text: text } : ing;
      });
    });
  }

  function removeIngredient(id) {
    setHasEditedIngredients(true);
    setMealIngredients(function(prev) {
      return prev.filter(function(ing) { return ing.id !== id; });
    });
  }

  // Structured ingredient editing functions
  function updateIngredientQuantity(id, newQuantity) {
    setHasEditedIngredients(true);
    setMealIngredients(function(prev) {
      return prev.map(function(ing) {
        if (ing.id !== id || !ing.isStructured) return ing;
        
        var qty = parseFloat(newQuantity) || 0;
        var portionOption = (ing.portionOptions || []).find(function(p) { return p.id === ing.unit; });
        var gramsPerUnit = portionOption ? portionOption.grams_per_unit : 1;
        var newGrams = qty * gramsPerUnit;
        var perG = ing._perG || { cal: 0, protein: 0, carbs: 0, fat: 0 };
        
        return {
          ...ing,
          quantity: qty,
          grams: Math.round(newGrams),
          calories: Math.round(newGrams * perG.cal),
          protein: Math.round(newGrams * perG.protein),
          carbs: Math.round(newGrams * perG.carbs),
          fat: Math.round(newGrams * perG.fat)
        };
      });
    });
  }

  function updateIngredientUnit(id, newUnit) {
    setHasEditedIngredients(true);
    setMealIngredients(function(prev) {
      return prev.map(function(ing) {
        if (ing.id !== id || !ing.isStructured) return ing;
        
        var portionOption = (ing.portionOptions || []).find(function(p) { return p.id === newUnit; });
        var gramsPerUnit = portionOption ? portionOption.grams_per_unit : 1;
        var newGrams = ing.quantity * gramsPerUnit;
        var perG = ing._perG || { cal: 0, protein: 0, carbs: 0, fat: 0 };
        
        return {
          ...ing,
          unit: newUnit,
          grams: Math.round(newGrams),
          calories: Math.round(newGrams * perG.cal),
          protein: Math.round(newGrams * perG.protein),
          carbs: Math.round(newGrams * perG.carbs),
          fat: Math.round(newGrams * perG.fat)
        };
      });
    });
  }

  // Add structured ingredient from USDA search
  function addStructuredIngredient(food) {
    var portionOptions = food.portion_options || [
      { id: 'g', label: 'grams', grams_per_unit: 1, is_estimate: false }
    ];
    
    var defaultUnit = portionOptions[0]?.id || 'g';
    var defaultOption = portionOptions.find(function(p) { return p.id === defaultUnit; }) || portionOptions[0];
    var defaultQty = 100;
    var grams = defaultQty * (defaultOption?.grams_per_unit || 1);
    
    // Get per-gram nutrition, computing cal_per_g from macros if missing (4/4/9 rule)
    var cal_per_g = food.cal_per_g || 0;
    var protein_per_g = food.protein_per_g || 0;
    var carbs_per_g = food.carbs_per_g || 0;
    var fat_per_g = food.fat_per_g || 0;
    
    if (cal_per_g === 0 && (protein_per_g > 0 || carbs_per_g > 0 || fat_per_g > 0)) {
      cal_per_g = (protein_per_g * 4) + (carbs_per_g * 4) + (fat_per_g * 9);
    }
    
    var perG = {
      cal: cal_per_g,
      protein: protein_per_g,
      carbs: carbs_per_g,
      fat: fat_per_g
    };
    
    var newIngredient = {
      id: Date.now(),
      name: food.name,
      food_id: food.food_id,
      quantity: defaultQty,
      unit: defaultUnit,
      grams: Math.round(grams),
      calories: Math.round(grams * perG.cal),
      protein: Math.round(grams * perG.protein),
      carbs: Math.round(grams * perG.carbs),
      fat: Math.round(grams * perG.fat),
      _perG: perG,
      portionOptions: portionOptions,
      isStructured: true
    };
    
    setMealIngredients(function(prev) {
      return prev.concat([newIngredient]);
    });
    
    setHasEditedIngredients(true);
    setAddingIngredient(false);
    setIngredientSearchQuery('');
    setIngredientSearchResults([]);
  }

  // Update editable macro
  function updateEditableMacro(key, value) {
    var numValue = parseInt(value) || 0;
    setEditableMacros(function(prev) {
      var updated = {};
      for (var k in prev) {
        updated[k] = prev[k];
      }
      updated[key] = Math.max(0, numValue);
      return updated;
    });
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col" data-testid="food-confirm">
      {/* Fixed header — single `px-4 pb-4` wrapper inside `header-safe-top`
          matches the Home / Plan / Grocery / Settings / Meals rhythm. */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="food-confirm-back">
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-heading font-semibold text-lg">Confirm Food</h1>
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Food identity */}
        <div className="mb-7">
          {isManualEntry ? (
            <input
              type="text"
              value={editableName}
              onChange={function(e) { setEditableName(e.target.value); }}
              onFocus={scrollFieldIntoView}
              placeholder="Enter food name"
              className="font-heading font-bold text-2xl text-gray-900 w-full bg-transparent border-b-2 border-gray-200 focus:border-accent outline-none pb-1"
              data-testid="manual-food-name-input"
            />
          ) : (
            <h2 className="font-heading font-bold text-2xl text-gray-900 leading-tight">{food.name}</h2>
          )}
          {/* Restaurant takes visual priority (accent); otherwise brand/package size. */}
          {food.restaurant ? (
            <p className="text-sm text-accent font-medium mt-1" data-testid="food-restaurant">{food.restaurant}</p>
          ) : (function() {
            var parts = [];
            if (food.brand) parts.push(food.brand);
            if (food.package_size) parts.push(food.package_size);
            if (parts.length > 0) {
              return <p className="text-sm text-gray-500 mt-1 leading-relaxed" data-testid="food-subtitle">{parts.join(' · ')}</p>;
            }
            return null;
          })()}
        </div>

        {/* Compact serving control — quantity +/- with inline unit (no oversized
            card, and the raw FatSecret serving sentence is intentionally not shown). */}
        <div className="mb-8">
          {unitLoading ? (
            <div className="flex flex-col items-center justify-center py-6 gap-3">
              <Loader2 size={28} className="text-accent animate-spin" />
              <p className="text-sm text-gray-400">Loading serving options...</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <button onClick={decrementQuantity} disabled={quantity <= _qtyMin} className="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform" data-testid="quantity-decrease">
                  <Minus size={18} className="text-gray-700" />
                </button>

                {/* Quantity + Unit as one block */}
                <div className="flex items-baseline gap-1.5 justify-center">
                  {isEditingQuantity ? (
                    <input
                      ref={quantityInputRef}
                      type="number"
                      value={editQuantityValue}
                      onChange={function(e) { setEditQuantityValue(e.target.value); }}
                      onBlur={handleQuantityEditSubmit}
                      onKeyDown={function(e) { if (e.key === 'Enter') handleQuantityEditSubmit(); }}
                      step={_qtyStep}
                      min={_qtyEditMin}
                      max={_qtyEditMax}
                      className="w-16 text-center font-mono text-2xl font-bold text-gray-900 bg-white border-2 border-accent rounded-lg focus:outline-none"
                      data-testid="quantity-input"
                    />
                  ) : (
                    <button
                      onClick={function() { setIsEditingQuantity(true); setEditQuantityValue(String(quantity)); }}
                      className="font-mono text-2xl font-bold text-gray-900 hover:text-accent cursor-pointer transition-colors"
                      title="Tap to edit quantity"
                      data-testid="quantity-display"
                    >
                      {quantity}
                    </button>
                  )}

                  {/* Unit label — tappable dropdown for all items with multiple units */}
                  {hasMultipleUnits ? (
                    <button
                      onClick={function() { setShowUnitPicker(!showUnitPicker); setShowDatePicker(false); setShowMealPicker(false); }}
                      className="text-base font-semibold text-accent hover:text-accent/80 transition-colors flex items-center gap-1"
                      data-testid="unit-selector"
                    >
                      {unitLabel(selectedUnit, quantity)}
                      <ChevronDown size={14} className="text-accent/60" />
                    </button>
                  ) : (
                    <span className="text-base font-semibold text-gray-600" data-testid="unit-label">{unitLabel(selectedUnit, quantity)}</span>
                  )}
                </div>

                <button onClick={incrementQuantity} disabled={quantity >= _qtyBtnMax} className="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform" data-testid="quantity-increase">
                  <Plus size={18} className="text-gray-700" />
                </button>
              </div>

              {/* Unit picker dropdown — unified for barcode and search items */}
              {showUnitPicker && hasMultipleUnits && (
                <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {isPackagedItem ? (
                    /* Packaged items (barcode + branded): use package/serving options */
                    barcodeUnitOptions.map(function(option) {
                      return (
                        <button
                          key={option.unit}
                          onClick={function() { setSelectedUnit(option.unit); setShowUnitPicker(false); setQuantity(1); }}
                          className={'w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ' + (option.unit === selectedUnit ? 'bg-accent/5 text-accent font-medium' : 'text-gray-900')}
                          data-testid={'unit-option-' + option.unit}
                        >
                          <span>{option.label}</span>
                          {option.unit === selectedUnit && <Check size={16} className="text-accent" />}
                        </button>
                      );
                    })
                  ) : (
                    /* Non-barcode items: use standard servingOptions */
                    servingOptions.map(function(option) {
                      var unitKey = option.unit;
                      return (
                        <button
                          key={unitKey}
                          onClick={function() { handleUnitChange(unitKey); }}
                          className={'w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ' + (unitKey === selectedUnit ? 'bg-accent/5 text-accent font-medium' : 'text-gray-900')}
                          data-testid={'unit-option-' + unitKey}
                        >
                          <span>{formatUnit(unitKey)}</span>
                          {unitKey === selectedUnit && <Check size={16} className="text-accent" />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Nutrition */}
        {isCustomMeal ? (
          /* Custom / Manual entries keep the editable macro card so users can
             type calories/protein/carbs/fat and see the computed-calorie check. */
          <div className="bg-accent/5 rounded-xl p-4 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Nutrition Info</p>
            <div className="flex justify-center mb-4">
              <div className="text-center">
                {quantity === 1 ? (
                  <input
                    type="number"
                    value={formatMacro(editableMacros.calories)}
                    onChange={function(e) { updateEditableMacro('calories', e.target.value); }}
                    onFocus={scrollFieldIntoView}
                    className="w-28 text-center font-mono text-4xl font-bold text-gray-900 bg-white border border-gray-200 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent/20 outline-none"
                    min="0"
                    data-testid="nutrition-calories"
                  />
                ) : (
                  <p className="font-mono text-4xl font-bold text-gray-900" data-testid="nutrition-calories">{calories}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">calories{quantity !== 1 && isScalableUnit(selectedUnit) ? ' (per ' + unitDisplayLabel(selectedUnit) + ': ' + formatMacro(editableMacros.calories) + ')' : ''}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-white rounded-lg">
                {quantity === 1 ? (
                  <input
                    type="number"
                    value={formatMacro(editableMacros.protein)}
                    onChange={function(e) { updateEditableMacro('protein', e.target.value); }}
                    onFocus={scrollFieldIntoView}
                    className="w-full text-center font-mono text-lg font-semibold text-gray-900 bg-transparent border border-gray-200 rounded focus:border-accent outline-none"
                    min="0"
                  />
                ) : (
                  <p className="font-mono text-lg font-semibold text-gray-900">{protein}g</p>
                )}
                <p className="text-xs text-gray-500">Protein</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                {quantity === 1 ? (
                  <input
                    type="number"
                    value={formatMacro(editableMacros.carbs)}
                    onChange={function(e) { updateEditableMacro('carbs', e.target.value); }}
                    onFocus={scrollFieldIntoView}
                    className="w-full text-center font-mono text-lg font-semibold text-gray-900 bg-transparent border border-gray-200 rounded focus:border-accent outline-none"
                    min="0"
                  />
                ) : (
                  <p className="font-mono text-lg font-semibold text-gray-900">{carbs}g</p>
                )}
                <p className="text-xs text-gray-500">Carbs</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                {quantity === 1 ? (
                  <input
                    type="number"
                    value={formatMacro(editableMacros.fat)}
                    onChange={function(e) { updateEditableMacro('fat', e.target.value); }}
                    onFocus={scrollFieldIntoView}
                    className="w-full text-center font-mono text-lg font-semibold text-gray-900 bg-transparent border border-gray-200 rounded focus:border-accent outline-none"
                    min="0"
                  />
                ) : (
                  <p className="font-mono text-lg font-semibold text-gray-900">{fat}g</p>
                )}
                <p className="text-xs text-gray-500">Fat</p>
              </div>
            </div>
            {quantity !== 1 && (
              <p className="text-xs text-gray-400 text-center mt-3">
                Set quantity to 1 to edit base macros
              </p>
            )}
            {isManualEntry && quantity === 1 && (editableMacros.protein > 0 || editableMacros.carbs > 0 || editableMacros.fat > 0) && (
              <div className={'mt-3 px-3 py-2 rounded-lg text-center text-sm ' + (hasMismatch ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200')} data-testid="computed-calories-preview">
                <span className={hasMismatch ? 'text-amber-700' : 'text-emerald-700'}>
                  {'Macros = ' + computedCalories + ' cal'}
                  {hasMismatch ? ' (entered: ' + formatMacro(editableMacros.calories) + ')' : ''}
                </span>
              </div>
            )}
          </div>
        ) : (
          /* Normal food / restaurant / barcode: clean primary + quieter secondary. */
          <div className="mb-2">
            {unitLoading ? (
              <div className="flex flex-col items-start gap-3 py-2">
                <div className="w-24 h-9 bg-gray-200 rounded animate-pulse" />
                <div className="flex gap-6">
                  <div className="w-16 h-5 bg-gray-200 rounded animate-pulse" />
                  <div className="w-16 h-5 bg-gray-200 rounded animate-pulse" />
                  <div className="w-16 h-5 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-4xl font-bold text-gray-900" data-testid="nutrition-calories">{calories}</span>
                  <span className="text-base text-gray-400 font-medium">cal</span>
                </div>
                <div className="flex items-center gap-6 mt-3" data-testid="nutrition-macros">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-lg font-bold text-gray-900">{protein}g</span>
                    <span className="text-sm text-gray-500">Protein</span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-lg font-bold text-gray-900">{carbs}g</span>
                    <span className="text-sm text-gray-500">Carbs</span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-lg font-bold text-gray-900">{fat}g</span>
                    <span className="text-sm text-gray-500">Fat</span>
                  </div>
                </div>
                {secondaryNutrients.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400 mt-3" data-testid="secondary-nutrition">
                    {secondaryNutrients.map(function(s, i) {
                      return (
                        <React.Fragment key={s.key}>
                          {i > 0 && <span className="text-gray-300">·</span>}
                          <span data-testid={'secondary-' + s.key}>
                            {s.label} <span className="text-gray-500 font-medium">{s.value}</span>
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Ingredients section - only for custom meals with ingredients */}
        {(hasIngredients || mealIngredients.length > 0) && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">Ingredients ({mealIngredients.length})</p>
              <div className="flex items-center gap-2">
                {hasStructuredIngredients && !editingIngredients && (
                  <button
                    onClick={function() { setEditingIngredients(true); setShowIngredients(true); }}
                    className="text-xs text-accent font-medium flex items-center gap-1"
                  >
                    <Edit3 size={12} />
                    Edit
                  </button>
                )}
                {editingIngredients && (
                  <button
                    onClick={function() { setEditingIngredients(false); setAddingIngredient(false); }}
                    className="text-xs text-gray-500 font-medium flex items-center gap-1"
                  >
                    <X size={12} />
                    Done
                  </button>
                )}
                <button
                  onClick={function() { setShowIngredients(!showIngredients); }}
                  className="text-xs text-gray-400"
                >
                  {showIngredients ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            
            {showIngredients && (
              <>
                {/* Adding ingredient search */}
                {addingIngredient && (
                  <div className="mb-4">
                    <div className="relative mb-3">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={ingredientSearchQuery}
                        onChange={function(e) { setIngredientSearchQuery(e.target.value); }}
                        placeholder="Search foods..."
                        autoFocus
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-accent/20"
                      />
                    </div>
                    
                    {searchingIngredients && (
                      <div className="flex justify-center py-4">
                        <Loader2 size={20} className="text-accent animate-spin" />
                      </div>
                    )}
                    
                    {ingredientSearchResults.length > 0 && (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {ingredientSearchResults.map(function(food) {
                          return (
                            <button
                              key={food.food_id}
                              onClick={function() { addStructuredIngredient(food); }}
                              className="w-full p-2 text-left bg-white rounded-lg hover:bg-gray-50 border border-gray-100"
                            >
                              <p className="text-sm font-medium text-gray-900 truncate">{food.name}</p>
                              <p className="text-xs text-gray-500">
                                {Math.round((food.cal_per_g || 0) * 100)} cal per 100g
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    <button
                      onClick={function() { setAddingIngredient(false); setIngredientSearchQuery(''); setIngredientSearchResults([]); }}
                      className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {/* Ingredients list */}
                {!addingIngredient && mealIngredients.length > 0 ? (
                  <ul className="space-y-2">
                    {mealIngredients.map(function(ing) {
                      if (ing.isStructured) {
                        // Structured ingredient with macros from USDA
                        return (
                          <li key={ing.id} className="bg-white rounded-lg px-3 py-2.5 border border-gray-100">
                            {editingIngredients ? (
                              // Edit mode - show quantity/unit inputs
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                                  <p className="text-sm text-gray-900 font-medium flex-1 truncate">{ing.name}</p>
                                  <button
                                    onClick={function() { removeIngredient(ing.id); }}
                                    className="p-1 text-red-400 hover:text-red-500"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2 pl-3.5">
                                  <input
                                    type="number"
                                    value={ing.quantity}
                                    onChange={function(e) { updateIngredientQuantity(ing.id, e.target.value); }}
                                    className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg text-center focus:border-accent outline-none"
                                    min="0"
                                    step="0.5"
                                  />
                                  <select
                                    value={ing.unit}
                                    onChange={function(e) { updateIngredientUnit(ing.id, e.target.value); }}
                                    className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:border-accent outline-none"
                                  >
                                    {(ing.portionOptions || []).map(function(opt) {
                                      return (
                                        <option key={opt.id} value={opt.id}>{opt.label || opt.id}</option>
                                      );
                                    })}
                                  </select>
                                </div>
                                <p className="text-xs text-gray-500 pl-3.5">
                                  {Math.round(ing.calories)} cal • {Math.round(ing.protein)}g P • {Math.round(ing.carbs)}g C • {Math.round(ing.fat)}g F
                                </p>
                              </div>
                            ) : (
                              // View mode - show compact display
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 font-medium truncate">
                                    {ing.quantity} {ing.unit} {ing.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {Math.round(ing.calories)} cal • {Math.round(ing.protein)}g P • {Math.round(ing.carbs)}g C • {Math.round(ing.fat)}g F
                                  </p>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      } else {
                        // Legacy text-only ingredient
                        return (
                          <li key={ing.id} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={ing.text}
                              onChange={function(e) { updateIngredient(ing.id, e.target.value); }}
                              placeholder="e.g., 100g chicken breast"
                              className="flex-1 bg-white rounded-lg px-3 py-2.5 text-sm border border-gray-200 focus:border-accent focus:ring-1 focus:ring-accent/20 outline-none"
                            />
                            <button
                              onClick={function() { removeIngredient(ing.id); }}
                              className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 size={16} />
                            </button>
                          </li>
                        );
                      }
                    })}
                  </ul>
                ) : !addingIngredient ? (
                  <p className="text-sm text-gray-400 text-center py-2">No ingredients</p>
                ) : null}
                
                {/* Add ingredient button */}
                {!addingIngredient && (editingIngredients || !hasStructuredIngredients) && (
                  <button
                    onClick={function() { setAddingIngredient(true); }}
                    className="w-full mt-3 py-2.5 border-2 border-dashed border-accent/40 rounded-lg text-accent hover:bg-accent/5 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus size={18} />
                    <span className="text-sm font-medium">Add Ingredient</span>
                  </button>
                )}
                
                {/* Edit mode note */}
                {editingIngredients && (
                  <p className="text-xs text-amber-600 text-center mt-3">
                    Macros will update automatically when you change ingredients
                  </p>
                )}
                
                {!hasStructuredIngredients && !editingIngredients && (
                  <p className="text-xs text-gray-400 text-center mt-3">
                    Edit ingredient amounts above. Update macros manually if needed.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Log destination — compact single row (date · meal), no oversized card */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          {/* Date picker */}
          <div className="relative">
            <button onClick={function() { setShowDatePicker(!showDatePicker); setShowMealPicker(false); setShowUnitPicker(false); }} className="w-full px-3 py-2.5 bg-white rounded-xl border border-gray-200 flex items-center justify-between" data-testid="date-selector">
              <span className="flex items-center gap-2 text-sm text-gray-900 truncate">
                <Calendar size={15} className="text-gray-400 flex-shrink-0" />
                <span className="truncate">{currentDateLabel}</span>
              </span>
              <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
            </button>
            {showDatePicker && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto">
                {dateOptions.map(function(opt) {
                  return (
                    <button key={opt.value} onClick={function() { setSelectedDate(opt.value); setDateManuallyPicked(true); setShowDatePicker(false); }} className={'w-full p-2.5 text-left text-sm hover:bg-gray-50 ' + (opt.value === selectedDate ? 'bg-accent/5 text-accent font-medium' : 'text-gray-900')}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Meal type picker */}
          <div className="relative">
            <button onClick={function() { setShowMealPicker(!showMealPicker); setShowDatePicker(false); setShowUnitPicker(false); }} className="w-full px-3 py-2.5 bg-white rounded-xl border border-gray-200 flex items-center justify-between" data-testid="meal-selector">
              <span className="text-sm text-gray-900 truncate">{selectedMealType}</span>
              <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
            </button>
            {showMealPicker && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20">
                {mealSlots.map(function(slot) {
                  return (
                    <button key={slot} onClick={function() { setSelectedMealType(slot); setShowMealPicker(false); }} className={'w-full p-2.5 text-left text-sm hover:bg-gray-50 ' + (slot === selectedMealType ? 'bg-accent/5 text-accent font-medium' : 'text-gray-900')}>
                      {slot}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed bottom button — solid primary action matching Home */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-gray-100" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        <Button onClick={handleLog} disabled={loading || unitLoading || (calories === 0 && !isValidZeroCalProviderFood)} size="lg" className="w-full h-14 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold text-base shadow-sm disabled:opacity-50" data-testid="log-food-btn">
          {loading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : unitLoading ? (
            'Loading...'
          ) : (
            <>Log Food</>
          )}
        </Button>
      </div>

      {/* Macro-Calorie Mismatch Dialog */}
      {showMismatchDialog && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-end justify-center" data-testid="mismatch-dialog-overlay">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-6 pb-8 animate-in slide-in-from-bottom duration-200" data-testid="mismatch-dialog">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-1">Calorie Mismatch</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              {'Your entered calories (' + formatMacro(editableMacros.calories) + ') don\u2019t match the macros (' + computedCalories + ' cal).'}
            </p>

            <div className="space-y-3">
              <button
                onClick={handleMismatchAdjust}
                className="w-full p-4 bg-accent/5 border-2 border-accent rounded-xl text-left"
                data-testid="mismatch-adjust-btn"
              >
                <p className="font-semibold text-accent text-sm">Adjust calories to match macros</p>
                <p className="text-xs text-gray-500 mt-0.5">{'Set calories to ' + computedCalories + ' (P\u00d74 + C\u00d74 + F\u00d79)'}</p>
              </button>

              <button
                onClick={handleMismatchOverride}
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-left"
                data-testid="mismatch-override-btn"
              >
                <p className="font-medium text-gray-700 text-sm">Keep entered calories</p>
                <p className="text-xs text-gray-500 mt-0.5">{'Log with ' + formatMacro(editableMacros.calories) + ' cal as entered'}</p>
              </button>

              <button
                onClick={function() { setShowMismatchDialog(false); }}
                className="w-full py-3 text-sm text-gray-400 text-center"
                data-testid="mismatch-cancel-btn"
              >
                Go back and edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FoodConfirm;
